
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, AlertTitle, Box, Button, Dialog, DialogContent, DialogTitle, Divider, Fade, Grid, IconButton, Paper, TextField, Typography } from "@mui/material";
import { getOrderById, getOrderByOrderId } from "../../State/Order/Action";
import { createPayment, updatePayment } from "../../State/Payment/Action";
import OrderTracker from "../Order/OrderTracker";
import CartItem from "../Cart/CartItem";
import { useConvertPrice } from "../../../Hooks/useConvertPrice";
import { Close } from "@mui/icons-material";
import defaultImage from "../../../Common/productDefaultImage.png";

const PaymentSuccess = () => {
  const [paymentId, setPaymentId] = useState();
  const dispatch = useDispatch();
  const { orderId } = useParams();
  const { order } = useSelector((store) => store);
  const navigate = useNavigate();
  const [paymentStatus, setPaymentStatus] = useState();
  const { state } = useLocation();
  const cart = state?.cart;
  const paymentCreated = useRef(false); // flag
  const { convert, selectedCurrency } = useConvertPrice();
  const [openCustomDialog, setOpenCustomDialog] = useState(false);
  const [selectedCustomSize, setSelectedCustomSize] = useState(null);

  // useEffect(() => {
  //   const urlParams = new URLSearchParams(window.location.search);
  //   const paymentIdFromURL = urlParams.get("razorpay_payment_id");
  //   const statusFromURL = urlParams.get("razorpay_payment_link_status");

  //   if (paymentIdFromURL) {
  //     setPaymentId(paymentIdFromURL);
  //     setPaymentStatus(statusFromURL);
  //   }
  // }, []);

  useEffect(() => {
    if (orderId) {
      // dispatch(getOrderById(orderId));
      dispatch(getOrderByOrderId(orderId));
    }
  }, [dispatch, orderId]);

  useEffect(() => {
    if (orderId && cart && !paymentCreated.current) {
      dispatch(createPayment(orderId, cart));
      paymentCreated.current = true; // prevent duplicate
    }
  }, [orderId, dispatch, cart]);

  // useEffect(() => {
  //   if (order?.order?.id && paymentId) {
  //     dispatch(updatePayment({ orderId, paymentId }));
  //   }
  // }, [order?.order?.id, paymentId, dispatch]);

  useEffect(() => {
    // Push a dummy state to history so browser back will trigger popstate
    window.history.pushState(null, document.title, window.location.href);

    const handlePopState = () => {
      // When back button is pressed, force redirect to /cart
      navigate("/cart", { replace: true });
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);

  const handleOpenCustomDialog = (sizes, productId) => {
    // ✅ Find from sizes list by productId
    const sizeData = sizes?.find((item) => item?.product?.id === productId) || null;
    setSelectedCustomSize(sizeData);
    setOpenCustomDialog(true);
  };

  const handleCloseCustomDialog = () => {
    setOpenCustomDialog(false);
    setSelectedCustomSize(null);
  };


  const totalSavings = order?.orders?.reduce((sum, o) => sum + (o?.totalDiscount || 0), 0);
  const hasSavings = totalSavings > 0;


  return (

    <Box sx={{
      px: { xs: 2, sm: 3, md: 4 },
      py: { xs: 3, sm: 4, md: 5 },
      bgcolor: "#fafafa",
      minHeight: "80vh"
    }}>
      {/* Success Alert */}
      <div className="mb-4 justify-content-center items-center justify-center align-self-center flex sticky-top">
        <Alert variant="filled" severity="success" sx={{ width: "100%", maxWidth: 600 }}>
          <AlertTitle>Payment Success</AlertTitle>
          Congratulations! Your order has been Placed.
        </Alert>
      </div>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4 },
          borderRadius: "16px",
          border: "1px solid #e5e7eb",
          background: "linear-gradient(135deg, #ffffff 0%, #fafafa 100%)",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
        }}
      >
        {/* Progress Tracker */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            fontWeight="600"
            color="#1f2937"
            sx={{
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
              mb: 3,
              textAlign: "center"
            }}
          >
            Order Details
          </Typography>

          <OrderTracker activeStep={1} />
        </Box>

        {/* Step Content */}
        <Box sx={{ mt: 4 }}>
          <Box >

            {/* Products and Price Section */}
            <Grid container spacing={3}>
              {/* Products */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                    background: "linear-gradient(135deg, #ffffff 0%, #fafafa 100%)",
                    boxShadow: "0 2px 4px -1px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.06)"
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight="600"
                    color="#374151"
                    sx={{ mb: 2, fontSize: "1.1rem" }}
                  >
                    Order Items
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    {order?.orders?.map((singleOrder) =>
                      singleOrder?.orderItems?.map((item) => (
                        <Box
                          key={item.id}
                          p={2}
                          mb={2}
                          sx={{
                            borderRadius: "12px",
                            bgcolor: "white",
                            boxShadow: "0 2px 4px -1px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.06)",
                            border: "1px solid #e5e7eb",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.05)",
                              transform: "translateY(-1px)",
                            },
                          }}
                        >
                          <Grid container spacing={2}>
                            {/* Product Image */}
                            <Grid item xs={12} sm={3}>
                              <Box
                                component="img"
                                src={item?.product?.images?.find((img) => img?.isPrimary && img?.productColor?.color?.name === item?.variant?.productColor?.color?.name)?.fullUrl ||
                                  defaultImage}
                                alt={item?.product?.title}
                                onClick={() => navigate(`/product/${item?.product?.id}`)}
                                sx={{
                                  width: "80%",
                                  height: "auto",
                                  cursor: "pointer",
                                  objectFit: "cover",
                                  aspectRatio: "3/4",
                                  transition: "transform 0.2s ease-in-out",
                                  "&:hover": {
                                    transform: "scale(1.02)",
                                  },
                                }}
                              />
                            </Grid>

                            {/* Product Info */}
                            <Grid item xs={12} sm={9}>
                              <Box
                                display="flex"
                                flexDirection="column"
                                justifyContent="space-between"
                                height="100%"
                              >
                                {/* Title and Brand */}
                                <Box
                                >
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight="600"
                                    color="#1f2937"
                                    onClick={() => navigate(`/product/${item?.product?.id}`)}
                                    sx={{
                                      fontSize: { xs: "0.8rem", sm: "1rem" },
                                      mb: 0.5,
                                      lineHeight: 1.3,
                                      cursor: "pointer"
                                    }}
                                  >
                                    {item?.product?.title}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="#6b7280"
                                    fontWeight="600"
                                    onClick={() => navigate(`/collections/brand/${item?.product?.brand?.name}`)}
                                    sx={{
                                      fontSize: "0.8rem",
                                      mb: 1,
                                      letterSpacing: "0.5px",
                                      cursor: "pointer"
                                    }}
                                  >
                                    {item?.product?.brand?.name?.toUpperCase()}
                                  </Typography>

                                  {/* Size & Color */}
                                  <Box
                                    mt={1}
                                    mb={1}
                                    sx={{
                                      display: "flex",
                                      gap: 2,
                                      flexWrap: "wrap"
                                    }}
                                  >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontWeight: "500", fontSize: "0.75rem" }}
                                      >
                                        Size:
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        fontWeight="600"
                                        sx={{
                                          backgroundColor: "#f3f4f6",
                                          px: 1.5,
                                          py: 0.25,
                                          borderRadius: "6px",
                                          color: "#374151",
                                          fontSize: "0.75rem",
                                          cursor: item?.size?.toLowerCase() === "custom tailor" ? "pointer" : "default"
                                        }}
                                        onClick={() => {
                                          if (item?.size?.toLowerCase() === "custom tailor") {
                                            handleOpenCustomDialog(singleOrder?.customSizes, item?.product?.id); // 👈 yaha custom size object pass karo
                                          }
                                        }}
                                      >
                                        {item?.size}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontWeight: "500", fontSize: "0.75rem" }}
                                      >
                                        Color:
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        fontWeight="600"
                                        sx={{
                                          backgroundColor: "#f3f4f6",
                                          px: 1.5,
                                          py: 0.25,
                                          borderRadius: "6px",
                                          color: "#374151",
                                          fontSize: "0.75rem"
                                        }}
                                      >
                                        {item?.color}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  {/* Pricing */}
                                  <Box>
                                    <Typography
                                      variant="h6"
                                      color="success.main"
                                      fontWeight="700"
                                      sx={{ fontSize: "1.25rem" }}
                                    >
                                      {convert(item?.orderPrice)} {selectedCurrency}
                                      {/* ₹{item?.variant?.payableAmount} */}
                                    </Typography>
                                  </Box>
                                  {item?.size?.toLowerCase() === "custom tailor" && (
                                    <Typography
                                      variant="body2"
                                      color="#e02323ff"
                                      fontWeight="500"
                                      sx={{
                                        fontSize: "0.8rem",
                                      }}
                                    >
                                      Customization Charge: {convert(item?.product?.customizationPrice)} {selectedCurrency}
                                    </Typography>
                                  )}

                                </Box>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      )))}
                  </Box>
                </Paper>
              </Grid>

              {/* Price Details */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                    background: "linear-gradient(135deg, #ffffff 0%, #fafafa 100%)",
                    boxShadow: "0 2px 4px -1px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.06)"
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight="600"
                    color="#374151"
                    gutterBottom
                    sx={{ fontSize: "1.1rem" }}
                  >
                    PRICE DETAILS
                  </Typography>
                  <Divider sx={{ borderColor: "#e5e7eb" }} />

                  <Box mt={2} mb={3}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography sx={{ fontSize: "0.875rem", color: "#6b7280" }}>
                        Total Items
                      </Typography>
                      <Typography sx={{ fontSize: "0.875rem", fontWeight: "500" }}>
                        {order?.orders?.reduce((sum, o) => sum + (o?.totalItem || 0), 0)}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography sx={{ fontSize: "0.875rem", color: "#6b7280" }}>
                        Total Price
                      </Typography>
                      <Typography sx={{ fontSize: "0.875rem", fontWeight: "500" }}>
                        {convert(order?.orders?.reduce((sum, o) => sum + (o?.totalPrice || 0), 0))} {selectedCurrency}
                        {/* {convert(order?.order?.totalPrice)} {selectedCurrency} */}
                        {/* ₹{order?.order?.totalPrice} */}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="success.main" sx={{ fontSize: "0.875rem", fontWeight: "500" }}>
                        Total Discount
                      </Typography>
                      <Typography color="success.main" sx={{ fontSize: "0.875rem", fontWeight: "500" }}>
                        {"-" + convert(order?.orders?.reduce((sum, o) => sum + (o?.totalDiscount || 0), 0)) + " " + selectedCurrency}
                        {/* {convert(order?.order?.totalDiscount || 0)} {selectedCurrency} */}
                        {/* ₹{order?.order?.totalDiscount || 0} */}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="success.main" fontWeight="500" sx={{ fontSize: "0.875rem" }}>
                        Delivery Charge
                      </Typography>
                      <Typography color="success.main" fontWeight="500" sx={{ fontSize: "0.875rem" }}>
                        Free
                      </Typography>
                    </Box>
                    {order?.orders?.reduce((sum, o) => sum + (o?.totalCustomizationCharge || 0), 0) > 0 && (
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography color="#e02323ff" fontWeight="500" sx={{ fontSize: "0.875rem" }}>
                          Customization Charge
                        </Typography>
                        <Typography color="#e02323ff" fontWeight="500" sx={{ fontSize: "0.875rem" }}>
                          {"+" + convert(order?.orders?.reduce((sum, o) => sum + (o?.totalCustomizationCharge || 0), 0)) + " " + selectedCurrency}
                          {/* {cart?.cart?.customizationCharge} */}
                        </Typography>
                      </Box>
                    )}
                    <Divider sx={{ my: 2, borderColor: "#e5e7eb" }} />
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="subtitle1" fontWeight="600" color="#1f2937">
                        Amount Paid
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="600" color="#1f2937">
                        {convert(order?.orders?.reduce((sum, o) => sum + (o?.orderTotalPrice || 0), 0))} {selectedCurrency}
                        {/* {convert(order?.order?.orderTotalPrice)} {selectedCurrency} */}
                        {/* ₹{order?.order?.orderTotalPrice} */}
                      </Typography>
                    </Box>

                    {hasSavings && (
                      <Fade in timeout={1000}>
                        <Typography
                          variant="caption"
                          color="success.main"
                          sx={{
                            textAlign: "center",
                            mt: 1,
                            p: 1,
                            backgroundColor: "#f0fdf4",
                            borderRadius: "6px",
                            fontWeight: "500",
                            fontSize: "0.75rem"
                          }}
                        >
                          🎉 You saved {convert(totalSavings)} {selectedCurrency} on this order!
                          {/* 🎉 You saved ₹{totalSavings} on this order! */}
                        </Typography>
                      </Fade>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Addresses Section */}
            <Grid container spacing={3} sx={{ mt: 3 }}>
              {/* Shipping Address */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                    background: "linear-gradient(135deg, #ffffff 0%, #fafafa 100%)",
                    boxShadow: "0 2px 4px -1px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.06)"
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight="600"
                    color="#374151"
                    sx={{ mb: 2, fontSize: "1.1rem" }}
                  >
                    Shipping Address
                  </Typography>

                  {/* Address Details */}
                  <Box sx={{ space: 1 }}>
                    <Box sx={{ display: "flex", mb: 1 }}>
                      <Typography sx={{ fontSize: "0.875rem", color: "#6b7280", fontWeight: "500", minWidth: "100px" }}>
                        Name:
                      </Typography>
                      <Typography sx={{ fontSize: "0.875rem", color: "#1f2937", fontWeight: "600" }}>
                        {order?.orders[0]?.shippingAddress?.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", mb: 1 }}>
                      <Typography sx={{ fontSize: "0.875rem", color: "#6b7280", fontWeight: "500", minWidth: "100px" }}>
                        Phone:
                      </Typography>
                      <Typography sx={{ fontSize: "0.875rem", color: "#1f2937" }}>
                        {order?.orders[0]?.shippingAddress?.mobile}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", mb: 1 }}>
                      <Typography sx={{ fontSize: "0.875rem", color: "#6b7280", fontWeight: "500", minWidth: "100px" }}>
                        Address:
                      </Typography>
                      <Typography sx={{ fontSize: "0.875rem", color: "#1f2937" }}>
                        {order?.orders[0]?.shippingAddress?.addressLine1},{order?.orders[0]?.shippingAddress?.postOffice} ,{order?.orders[0]?.shippingAddress?.city}, {order?.orders[0]?.shippingAddress?.state},{order?.orders[0]?.shippingAddress?.country}, {order?.orders[0]?.shippingAddress?.zipcode}
                      </Typography>
                    </Box>
                    {order?.orders[0]?.shippingAddress?.addressLine2 && (
                      <Box sx={{ display: "flex", mb: 1 }}>
                        <Typography sx={{ fontSize: "0.875rem", color: "#6b7280", fontWeight: "500", minWidth: "100px" }}>
                          Address 2:
                        </Typography>
                        <Typography sx={{ fontSize: "0.875rem", color: "#1f2937" }}>
                          {order?.orders[0]?.shippingAddress?.addressLine2},{order?.orders[0]?.shippingAddress?.postOffice} ,{order?.orders[0]?.shippingAddress?.city}, {order?.orders[0]?.shippingAddress?.state},{order?.orders[0]?.shippingAddress?.country}, {order?.orders[0]?.shippingAddress?.zipcode}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: "flex", mb: 1 }}>
                      <Typography sx={{ fontSize: "0.875rem", color: "#6b7280", fontWeight: "500", minWidth: "100px" }}>
                        Near:
                      </Typography>
                      <Typography sx={{ fontSize: "0.875rem", color: "#1f2937" }}>
                        {order?.orders[0]?.shippingAddress?.nearestLocation},{order?.orders[0]?.shippingAddress?.postOffice} ,{order?.orders[0]?.shippingAddress?.city}, {order?.orders[0]?.shippingAddress?.state},{order?.orders[0]?.shippingAddress?.country}, {order?.orders[0]?.shippingAddress?.zipcode}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Billing Address */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                    background: "linear-gradient(135deg, #ffffff 0%, #fafafa 100%)",
                    boxShadow: "0 2px 4px -1px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.06)"
                  }}
                >
                  {/* Header Row: Billing Address + Edit Icon */}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                    <Typography
                      variant="h6"
                      fontWeight="600"
                      color="#374151"
                      sx={{ fontSize: "1.1rem" }}
                    >
                      Billing Address
                    </Typography>
                  </Box>

                  {/* Address Details */}
                  <Box sx={{ space: 1 }}>
                    <Box sx={{ display: "flex", mb: 1 }}>
                      <Typography sx={{ fontSize: "0.875rem", color: "#6b7280", fontWeight: "500", minWidth: "100px" }}>
                        Name:
                      </Typography>
                      <Typography sx={{ fontSize: "0.875rem", color: "#1f2937", fontWeight: "600" }}>
                        {order?.orders[0]?.shippingAddress?.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", mb: 1 }}>
                      <Typography sx={{ fontSize: "0.875rem", color: "#6b7280", fontWeight: "500", minWidth: "100px" }}>
                        Phone:
                      </Typography>
                      <Typography sx={{ fontSize: "0.875rem", color: "#1f2937" }}>
                        {order?.orders[0]?.shippingAddress?.billingMobile}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", mb: 1 }}>
                      <Typography sx={{ fontSize: "0.875rem", color: "#6b7280", fontWeight: "500", minWidth: "100px" }}>
                        Address:
                      </Typography>
                      <Typography sx={{ fontSize: "0.875rem", color: "#1f2937" }}>
                        {order?.orders[0]?.shippingAddress?.billingAddressLine1},{order?.orders[0]?.shippingAddress?.billingPostOffice} ,{order?.orders[0]?.shippingAddress?.billingCity}, {order?.orders[0]?.shippingAddress?.billingState},{order?.orders[0]?.shippingAddress?.billingCountry}, {order?.orders[0]?.shippingAddress?.billingZipcode}
                      </Typography>
                    </Box>
                    {order?.orders[0]?.shippingAddress?.billingAddressLine2 && (
                      <Box sx={{ display: "flex", mb: 1 }}>
                        <Typography sx={{ fontSize: "0.875rem", color: "#6b7280", fontWeight: "500", minWidth: "100px" }}>
                          Address 2:
                        </Typography>
                        <Typography sx={{ fontSize: "0.875rem", color: "#1f2937" }}>
                          {order?.orders[0]?.shippingAddress?.billingAddressLine2},{order?.orders[0]?.shippingAddress?.billingPostOffice} ,{order?.orders[0]?.shippingAddress?.billingCity}, {order?.orders[0]?.shippingAddress?.billingState},{order?.orders[0]?.shippingAddress?.billingCountry}, {order?.orders[0]?.shippingAddress?.billingZipcode}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: "flex", mb: 1 }}>
                      <Typography sx={{ fontSize: "0.875rem", color: "#6b7280", fontWeight: "500", minWidth: "100px" }}>
                        Near:
                      </Typography>
                      <Typography sx={{ fontSize: "0.875rem", color: "#1f2937" }}>
                        {order?.orders[0]?.shippingAddress?.billingNearestLocation},{order?.orders[0]?.shippingAddress?.billingPostOffice} ,{order?.orders[0]?.shippingAddress?.billingCity}, {order?.orders[0]?.shippingAddress?.billingState},{order?.orders[0]?.shippingAddress?.billingCountry}, {order?.orders[0]?.shippingAddress?.billingZipcode}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Paper>
      {/* Continue Shopping */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-6 justify-center items-center sm:items-center">
        <Button
          variant="contained"
          color="error"
          sx={{
            px: "2.5rem",
            py: ".7rem",
            width: { xs: "100%", sm: "auto" }
          }}
          onClick={() => navigate("/")}
        >
          Continue Shopping
        </Button>

        <Button
          variant="outlined"
          color="error"
          sx={{
            px: "2.5rem",
            py: ".7rem",
            width: { xs: "100%", sm: "auto" },
            "&:hover": {
              color: "white",
            },
            "&:active": {
              color: "white",
            },
          }}
          onClick={() => navigate(`/account/order-history`)}
        >
          View Orders
        </Button>
      </div>
      {/* size modal */}
      <Dialog open={openCustomDialog} onClose={handleCloseCustomDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Custom Size Details
          <IconButton
            aria-label="close"
            onClick={handleCloseCustomDialog}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {selectedCustomSize ? (
            <Grid container spacing={2}>
              <Grid item xs={12} >
                <TextField
                  label="Product Title"
                  value={selectedCustomSize?.product?.title}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              {Object.entries(selectedCustomSize)
                // unwanted fields hata do
                .filter(([key, value]) =>
                  value !== null &&
                  value !== undefined &&
                  value !== "" &&
                  !["id", "product", "createdAt"].includes(key)
                )
                .map(([key, value]) => {
                  // Label banane ka helper
                  const label = key
                    .replace(/([A-Z])/g, " $1")   // camelCase -> camel Case
                    .replace(/_/g, " ")           // snake_case -> snake case
                    .replace(/^./, (str) => str.toUpperCase()); // pehla letter capitalize

                  return (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                      <TextField
                        label={label}
                        value={String(value)}
                        size="small"
                        fullWidth
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  );
                })}
            </Grid>
          ) : (
            <Typography color="text.secondary">
              No custom size details found.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PaymentSuccess;
